import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { AccountModelPrivatization } from "./AccountModelPrivatization";
import { ActiveUser } from "../core/ActiveUser";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ClientModelPrivatization } from "./ClientModelPrivatization";
import { AccountLookupService } from "../../lookup/model/impl/AccountLookupService";
import { BranchLookupService } from "../../lookup/model/impl/BranchLookupService";
import { ClientLookupService } from "../../lookup/model/impl/ClientLookupService";
import { UserLookupService } from "../../lookup/model/impl/UserLookupService";
import { AccountPromiseLookupService } from "../../lookup/model/impl/AccountPromiseLookupService";
import { BranchPromiseLookupService } from "../../lookup/model/impl/BranchPromiseLookupService";
import { ClientPromiseLookupService } from "../../lookup/model/impl/ClientPromiseLookupService";
import { UserPromiseLookupService } from "../../lookup/model/impl/UserPromiseLookupService";
import { ClientTraderModelPrivatization } from "./ClientTraderModelPrivatization";
import { SystemConfigPromiseService } from "shared/auth-services";

import { BasePrivatizationService } from "./BasePrivatizationService";
import { StaticDataPrivatizationType } from "./enum/StaticDataPrivatizationType";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { SysFeatures } from "../core/SysFeatures";

@Injectable()
export class DealPrivatizationService extends BasePrivatizationService {
  private static _instance;
  private prefix = "__bfx_privatization";
  private activeUser: ActiveUser;

  private clientSegmentRole: boolean = false;
  private clientDataRole: boolean = false;
  private privateLimitOrders: boolean = false;
  private privateBenchmarkOrders: boolean = false;

  private privatizeClientComments: boolean;
  private privatizePrivateComments: boolean;

  private readyResolve;
  private readyReject;

  public readonly ready = new Promise<void>((res, rej) => {
    this.readyResolve = res;
    this.readyReject = rej;
  });

  public constructor(
    accountPromiseLookup: AccountPromiseLookupService,
    branchPromiseLookup: BranchPromiseLookupService,
    clientPromiseLookup: ClientPromiseLookupService,
    userPromiseLookup: UserPromiseLookupService,
    private userProfileService: ActiveUserProfilePromiseService,
    private sysConfigService: SystemConfigPromiseService,
    cmp: ClientModelPrivatization,
    amp: AccountModelPrivatization,
    ctmp: ClientTraderModelPrivatization,
  ) {
    super(cmp, amp, ctmp);

    Promise.all([
      accountPromiseLookup.isPrimed(),
      branchPromiseLookup.isPrimed(),
      clientPromiseLookup.isPrimed(),
      userPromiseLookup.isPrimed(),
      this.userProfileService.isPrimed(),
      this.sysConfigService.isPrimed(),
      cmp.ready,
      amp.ready,
      ctmp.ready,
    ]).then(
      () => {
        this.accountLookup = accountPromiseLookup.getBase();
        this.branchLookup = branchPromiseLookup.getBase();
        this.clientLookup = clientPromiseLookup.getBase();
        this.userLookup = userPromiseLookup.getBase();

        this.clientSegmentRole = this.sysConfigService.hardHasRole(
          "ROLE_VISIBILITY_CLIENT_SEGMENT",
        );
        this.clientDataRole = this.sysConfigService.hardHasRole("ROLE_VISIBILITY_CLIENT_DATA");
        this.privateLimitOrders = this.sysConfigService.hardHasRole(
          "ROLE_VISIBILITY_PRIVATE_LIMIT_ORDERS",
        );
        this.privateBenchmarkOrders = this.sysConfigService.hardHasRole(
          "ROLE_VISIBILITY_PRIVATE_BENCHMARK_ORDERS",
        );
        this.privatizePrivateComments =
          this.sysConfigService.hardGetSystemConfig(SysFeatures.privatisePrivateComments) == `Y`;
        this.privatizeClientComments =
          this.sysConfigService.hardGetSystemConfig(SysFeatures.privatiseClientComments) == `Y`;

        this.userProfileService.getActiveUserData().then(
          (activeUser) => {
            this.activeUser = activeUser;

            this.readyResolve();
          },
          () => {
            this.readyReject();
          },
        );
      },
      () => {
        this.readyReject();
      },
    );
  }

  public checkPrivatization(deal: CubiqTS.DealImpl): CubiqTS.DealImpl {
    if (deal == null) {
      logger.out.warn(
        `${this.constructor.name}.checkPrivatization() attempted to privatize null Deal`,
      );
      return deal;
    }

    const client = this.clientLookup.getClient(deal.clientCode);

    if (this.activeUser.userCode == deal.entryUserCode) {
      if (!client) {
        this.checkIdName(deal.clientCode, deal.clientName, deal.accountCode, deal.accountName);
        StaticDataPrivatizationType.setPrivatization(
          deal,
          StaticDataPrivatizationType.RestrictedIdName,
        );
        deal.clientCode = this.cmp.getRestrictedIdNameKey(deal.clientCode, deal.clientName);
        deal.accountCode = this.amp.getRestrictedIdNameKey(deal.accountCode, deal.accountName);
      }

      return deal;
    }

    if (
      deal.isPrivate &&
      this.activeUser.userCode != deal.entryUserCode &&
      ((CubiqTS.OrderType.BENCHMARK === deal.orderType && !this.privateBenchmarkOrders) ||
        (CubiqTS.OrderType.BENCHMARK !== deal.orderType && !this.privateLimitOrders))
    ) {
      StaticDataPrivatizationType.setPrivatization(deal, StaticDataPrivatizationType.Private);
      deal.clientCode = this.cmp.getPrivateKey(client);
      deal.clientName = this.cmp.getPrivateLabel();
      deal.accountCode = this.amp.getPrivateKey();
      deal.accountName = this.amp.getPrivateLabel();

      if (deal.clientTraderId != null || deal.clientTraderName != null) {
        deal.clientTraderId = this.ctmp.getPrivateId();
        deal.clientTraderName = this.ctmp.getPrivateLabel();
      }

      if (this.privatizeClientComments) deal.clientComment = this.cmp.getPrivateLabel();

      if (this.privatizePrivateComments) deal.privateComment = this.cmp.getPrivateLabel();

      deal.counterPartyDetails = this.privatizeCounterPartyDetails(
        deal.counterPartyDetails,
        this.amp.getPrivateLabel(),
        this.cmp.getClientContactIdForPrivatizationKey(deal.clientCode),
      );
    } else if (
      this.branchLookup.isPrivateBranchCode(deal.clientBranchCode) &&
      this.activeUser.branchCd != deal.clientBranchCode
    ) {
      StaticDataPrivatizationType.setPrivatization(deal, StaticDataPrivatizationType.BranchPrivate);
      deal.clientCode = this.cmp.getBranchPrivateKey(deal.clientBranchCode, client);
      deal.clientName = this.cmp.getBranchPrivateLabel(deal.clientBranchCode);
      deal.accountCode = this.amp.getBranchPrivateKey(deal.clientBranchCode);
      deal.accountName = this.amp.getBranchPrivateLabel(deal.clientBranchCode);

      if (deal.clientTraderId != null || deal.clientTraderName != null) {
        deal.clientTraderId = this.ctmp.getBranchPrivateId(deal.clientBranchCode);
        deal.clientTraderName = this.ctmp.getBranchPrivateLabel(deal.clientBranchCode);
      }

      if (this.privatizeClientComments)
        deal.clientComment = this.cmp.getBranchPrivateLabel(deal.clientBranchCode);

      if (this.privatizePrivateComments)
        deal.privateComment = this.cmp.getBranchPrivateLabel(deal.clientBranchCode);

      deal.counterPartyDetails = this.privatizeCounterPartyDetails(
        deal.counterPartyDetails,
        this.amp.getBranchPrivateLabel(deal.clientBranchCode),
        this.cmp.getClientContactIdForPrivatizationKey(deal.clientCode),
      );
    } else if (
      ((this.activeUser.isSales() &&
        !this.activeUser.clientIdsInSalesGroupSet.has(deal.clientCode)) ||
        (this.activeUser.isTrader() && !deal.isClientIsTraderVisible)) &&
      !this.clientSegmentRole &&
      !this.clientDataRole
    ) {
      StaticDataPrivatizationType.setPrivatization(deal, StaticDataPrivatizationType.Restricted);
      deal.clientCode = this.cmp.getRestrictedKey();
      deal.clientName = this.cmp.getRestrictedLabel();
      deal.accountCode = this.amp.getRestrictedKey();
      deal.accountName = this.amp.getRestrictedLabel();

      if (deal.clientTraderId != null || deal.clientTraderName != null) {
        deal.clientTraderId = this.ctmp.getRestrictedId();
        deal.clientTraderName = this.ctmp.getRestrictedLabel();
      }

      if (this.privatizeClientComments) deal.clientComment = this.cmp.getRestrictedLabel();

      if (this.privatizePrivateComments) deal.privateComment = this.cmp.getRestrictedLabel();

      deal.counterPartyDetails = this.privatizeCounterPartyDetails(
        deal.counterPartyDetails,
        this.amp.getRestrictedLabel(),
        this.cmp.getClientContactIdForPrivatizationKey(deal.clientCode),
      );
    } else if (
      ((this.activeUser.isSales() &&
        !this.activeUser.clientIdsInSalesGroupSet.has(deal.clientCode)) ||
        (this.activeUser.isTrader() && !deal.isClientIsTraderVisible)) &&
      this.clientSegmentRole &&
      !this.clientDataRole
    ) {
      StaticDataPrivatizationType.setPrivatization(
        deal,
        StaticDataPrivatizationType.RestrictedClientSegment,
      );
      this.checkClientSegment(deal.clientSegment);
      deal.clientCode = this.cmp.getRestrictedClientSegmentKey(deal.clientSegment);
      deal.clientName = deal.clientSegment;
      deal.accountCode = this.amp.getRestrictedClientSegmentKey(deal.clientSegment);
      deal.accountName = deal.clientSegment;

      if (deal.clientTraderId != null || deal.clientTraderName != null) {
        deal.clientTraderId = CubiqTS.Long.fromString(
          this.ctmp.getRestrictedClientSegmentKey(deal.clientSegment),
        );
        deal.clientTraderName = deal.clientSegment;
      }

      if (this.privatizeClientComments) deal.clientComment = deal.clientSegment;

      if (this.privatizePrivateComments) deal.privateComment = deal.clientSegment;

      deal.counterPartyDetails = this.privatizeCounterPartyDetails(
        deal.counterPartyDetails,
        deal.clientSegment,
        this.cmp.getClientContactIdForPrivatizationKey(deal.clientCode),
      );
    } else if (!client) {
      this.checkIdName(deal.clientCode, deal.clientName, deal.accountCode, deal.accountName);
      StaticDataPrivatizationType.setPrivatization(
        deal,
        StaticDataPrivatizationType.RestrictedIdName,
      );
      deal.clientCode = this.cmp.getRestrictedIdNameKey(deal.clientCode, deal.clientName);
      deal.accountCode = this.amp.getRestrictedIdNameKey(deal.accountCode, deal.accountName);
    }

    return deal;
  }

  public isReady(): Promise<void> {
    return this.ready;
  }
}
